/**
 * One place that decides how an order's delivery status looks, so the merchant
 * reads the same colour everywhere: the Orders table and the installed app.
 *
 *   blue    new order, nothing to do yet / being packed
 *   amber   the courier is coming or has been asked to come: keep the parcel ready
 *   purple  on its way to the customer
 *   green   delivered
 *   orange  a problem the merchant should look at (pickup or delivery failed, delayed)
 *   red     cancelled
 *   brown   coming back or came back (returned, RTO)
 */
export interface StatusLook {
	label: string;
	bg: string;
	fg: string;
	/** Strong colour for a stripe or dot. */
	accent: string;
}

const LOOKS = {
	blue: { bg: "#E3F0FF", fg: "#0B4FA8", accent: "#1976D2" },
	amber: { bg: "#FFF1CC", fg: "#7A5200", accent: "#F2A900" },
	purple: { bg: "#EDE4FF", fg: "#4A1FA8", accent: "#7C4DFF" },
	green: { bg: "#DDF5E3", fg: "#136B2C", accent: "#2E9E4F" },
	orange: { bg: "#FFE3D1", fg: "#A23A00", accent: "#F26B1D" },
	red: { bg: "#FDDCDC", fg: "#A11212", accent: "#D32F2F" },
	brown: { bg: "#EFE2D6", fg: "#6B3A12", accent: "#8D5524" },
	grey: { bg: "#ECECEC", fg: "#444444", accent: "#9E9E9E" },
};

const titleCase = (status: string) =>
	status.replace(/[_-]+/g, " ").toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

export function statusLook(status?: string | null): StatusLook {
	const raw = String(status ?? "").trim();
	if (!raw || raw === "-") return { label: "Processing", ...LOOKS.grey };
	const s = raw.toUpperCase();
	const label = s.startsWith("RTO") ? "RTO" + titleCase(raw.slice(3)) : titleCase(raw);

	// Order matters: "RTO Delivered", "Undelivered" and "Out For Delivery" all contain "DELIVER".
	if (s.includes("RTO") || s.includes("RETURN")) return { label, ...LOOKS.brown };
	if (s.includes("CANCEL")) return { label, ...LOOKS.red };
	if (s.includes("EXCEPTION") || s.includes("UNDELIVERED") || s.includes("DELAY") || s.includes("FAIL") || s.includes("LOST") || s.includes("DAMAGE"))
		return { label, ...LOOKS.orange };
	if (s.includes("OUT FOR DELIVERY") || s.includes("TRANSIT") || s.includes("PICKED") || s.includes("SHIPPED") || s.includes("DISPATCH") || s.includes("REACHED"))
		return { label, ...LOOKS.purple };
	if (s.includes("DELIVERED")) return { label, ...LOOKS.green };
	if (s.includes("PICKUP")) return { label, ...LOOKS.amber };
	return { label, ...LOOKS.blue };
}
